import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fmtConf = (c) => (c == null ? "—" : `${(c * 100).toFixed(0)}%`);
const typeLabel = (t) => (t || "related").replace(/_/g, " ");

// Searchable document target picker (title / AI summary / aliases / metadata).
function TargetPicker({ onSelect, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const search = useCallback((val) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!val.trim()) { setResults([]); return; }
      try {
        const r = await api.get("/knowledge/documents/search", { params: { q: val, limit: 8 } });
        setResults(r.data.documents || []); setOpen(true);
      } catch { setResults([]); }
    }, 250);
  }, []);

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
        <Icons.Search className="h-3.5 w-3.5 text-slate-400" />
        <input value={q} onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search documents to attach…" data-testid={testid}
          className="flex-1 text-xs outline-none bg-transparent" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-results`}>
          {results.map((d) => (
            <button key={d.id} onClick={() => { onSelect(d); setOpen(false); setQ(d.title); }}
              data-testid={`${testid}-result-${d.id}`}
              className="w-full text-left px-3 py-2 hover:bg-indigo-50 border-b border-slate-100 last:border-0">
              <div className="text-xs font-medium text-slate-700 truncate">{d.title}</div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {d.jurisdiction && <span className="text-[10px] bg-blue-50 text-blue-600 rounded px-1">{d.jurisdiction}</span>}
                {d.document_type && <span className="text-[10px] bg-slate-100 text-slate-500 rounded px-1">{d.document_type}</span>}
                {d.ai_summary && <span className="text-[10px] text-slate-400 truncate">{d.ai_summary.slice(0, 60)}…</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TargetPreview({ target }) {
  if (!target) return null;
  return (
    <div className="mt-1.5 rounded-lg bg-emerald-50/60 border border-emerald-100 p-2" data-testid="rel-target-preview">
      <div className="flex items-center gap-1.5">
        <Icons.FileCheck className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs font-medium text-emerald-800 truncate">{target.title}</span>
      </div>
      {target.ai_summary && <p className="text-[11px] text-slate-500 mt-1 line-clamp-3">{target.ai_summary}
        <span className="text-emerald-600 italic"> — AI summary (not authoritative)</span></p>}
    </div>
  );
}

export default function RelationshipWorkspace({ collectionId = null, onChanged }) {
  const [tab, setTab] = useState("proposals");
  const [rels, setRels] = useState([]);
  const [orphanReport, setOrphanReport] = useState(null);
  const [targets, setTargets] = useState({});        // pid -> selected target doc
  const [selected, setSelected] = useState(() => new Set());
  const [bulkTarget, setBulkTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadRels = useCallback(async () => {
    const r = await api.get("/knowledge/relationship-proposals", { params: { status: "pending", ...(collectionId ? { collection_id: collectionId } : {}) } });
    setRels(r.data.proposals || []);
  }, [collectionId]);
  const loadOrphans = useCallback(async () => {
    const r = await api.get("/knowledge/relationship-proposals/orphans");
    setOrphanReport(r.data);
  }, []);
  useEffect(() => { loadRels(); }, [loadRels]);
  useEffect(() => { if (tab === "orphans" && !orphanReport) loadOrphans(); }, [tab, orphanReport, loadOrphans]);

  const refresh = () => { loadRels(); loadOrphans(); onChanged?.(); };

  const approve = async (r, targetId) => {
    const tid = targetId || targets[r.id]?.id || r.target_item_id;
    if (!tid) { toast.error("Select a target document first (Change target)."); return; }
    setBusy(true);
    try {
      await api.post(`/knowledge/relationship-proposals/${r.id}/approve`, { target_item_id: tid, relationship_type: r.relationship_type });
      toast.success("Relationship approved & indexed");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };
  const reject = async (r) => {
    setBusy(true);
    try { await api.post(`/knowledge/relationship-proposals/${r.id}/reject`); toast.success("Rejected"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };
  const bulkApprove = async () => {
    if (!bulkTarget) { toast.error("Pick a shared target document for the selected relationships."); return; }
    if (selected.size === 0) { toast.error("Select at least one relationship."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/knowledge/relationship-proposals/bulk-approve`, {
        proposal_ids: Array.from(selected), target_item_id: bulkTarget.id });
      toast.success(`Approved ${r.data.approved_count} relationship(s)`);
      setSelected(new Set()); setBulkTarget(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(false); }
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const resolvable = rels.filter((r) => r.target_item_id);
  const unresolved = rels.filter((r) => !r.target_item_id);

  return (
    <div data-testid="relationship-workspace">
      <div className="flex items-center gap-2 mb-3">
        <button onClick={() => setTab("proposals")} data-testid="rel-tab-proposals"
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${tab === "proposals" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          Proposals · {rels.length}
        </button>
        <button onClick={() => setTab("orphans")} data-testid="rel-tab-orphans"
          className={`text-xs font-semibold px-3 py-1.5 rounded-full ${tab === "orphans" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          Orphan report{orphanReport ? ` · ${orphanReport.orphan_count}` : ""}
        </button>
      </div>

      {tab === "proposals" && (
        <div className="space-y-3">
          {/* bulk bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-2" data-testid="rel-bulk-bar">
              <span className="text-xs font-semibold text-indigo-700">{selected.size} selected</span>
              <div className="flex-1 max-w-md"><TargetPicker testid="rel-bulk-picker" onSelect={setBulkTarget} /></div>
              {bulkTarget && <span className="text-[11px] text-emerald-700 truncate max-w-[160px]">→ {bulkTarget.title}</span>}
              <button onClick={bulkApprove} disabled={busy} data-testid="rel-bulk-approve"
                className="text-xs font-semibold rounded-md px-3 py-1.5 bg-emerald-600 text-white disabled:opacity-40">Approve with shared target</button>
              <button onClick={() => setSelected(new Set())} className="text-xs text-slate-500">Clear</button>
            </div>
          )}
          {rels.length === 0 && <p className="text-sm text-slate-400 py-4" data-testid="rel-empty">No pending relationships.</p>}
          {[...unresolved, ...resolvable].map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 p-3" data-testid={`rel-card-${r.id}`}>
              <div className="flex items-start gap-2.5">
                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)}
                  data-testid={`rel-select-${r.id}`} className="mt-1 accent-indigo-600" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">{typeLabel(r.relationship_type)}</span>
                    {r.confidence != null && <span className="text-[10px] text-slate-400">conf {fmtConf(r.confidence)}</span>}
                    {!r.target_item_id && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">unresolved</span>}
                  </div>
                  <div className="text-xs text-slate-700 mt-1"><span className="text-slate-400">Source:</span> <span className="font-semibold">{r.source_title}</span></div>
                  <div className="text-xs text-slate-500 mt-0.5"><span className="text-slate-400">Proposed target:</span> {r.target_title || r.target_reference}</div>
                  {r.reason && <p className="text-[11px] text-slate-400 italic mt-1"><Icons.Sparkles className="h-3 w-3 inline mr-1 text-indigo-400" />{r.reason}</p>}
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                    <TargetPicker testid={`rel-picker-${r.id}`} onSelect={(d) => setTargets((t) => ({ ...t, [r.id]: d }))} />
                    <TargetPreview target={targets[r.id]} />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => approve(r)} disabled={busy || (!targets[r.id] && !r.target_item_id)} data-testid={`rel-approve-${r.id}`}
                    className="text-xs font-semibold rounded-md px-2.5 py-1 bg-emerald-600 text-white disabled:opacity-40">Approve</button>
                  <button onClick={() => reject(r)} disabled={busy} data-testid={`rel-reject-${r.id}`}
                    className="text-xs font-semibold rounded-md px-2.5 py-1 border border-slate-200 text-slate-600">Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "orphans" && (
        <div className="space-y-3" data-testid="rel-orphan-report">
          {!orphanReport ? <p className="text-sm text-slate-400 py-4">Loading…</p> : (
            <>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                <p className="font-semibold text-slate-700 mb-1">{orphanReport.orphan_count} unresolved relationships</p>
                <p className="text-[11px] text-slate-400">Documents referenced repeatedly but not in the Knowledge Center may be missing / duplicate authorities to import.</p>
                {(orphanReport.possible_missing_or_duplicate_targets || []).slice(0, 6).map((m, i) => (
                  <div key={i} className="flex items-center justify-between mt-1.5">
                    <span className="truncate">{m.title}</span>
                    <span className="text-[10px] bg-amber-50 text-amber-700 rounded px-1.5 py-0.5 shrink-0">referenced {m.referenced_by}×</span>
                  </div>
                ))}
              </div>
              {(orphanReport.orphans || []).slice(0, 60).map((o) => (
                <div key={o.id} className="rounded-lg border border-slate-200 p-3" data-testid={`orphan-${o.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium">{typeLabel(o.relationship_type)}</span>
                    <span className="text-[10px] text-slate-400">conf {fmtConf(o.confidence)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">{o.unmatched_reason}</span>
                  </div>
                  <div className="text-xs text-slate-700 mt-1"><span className="text-slate-400">Source:</span> {o.source_title}</div>
                  <div className="text-xs text-slate-500 mt-0.5"><span className="text-slate-400">Likely target:</span> {o.likely_target_title}</div>
                  {(o.suggested_targets || []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`orphan-suggestions-${o.id}`}>
                      <span className="text-[10px] text-slate-400 self-center">Attach:</span>
                      {o.suggested_targets.map((s) => (
                        <button key={s.id} onClick={() => approve({ id: o.id, relationship_type: o.relationship_type }, s.id)} disabled={busy}
                          data-testid={`orphan-attach-${o.id}-${s.id}`}
                          className="text-[11px] rounded-md px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                          {s.title.slice(0, 40)} <span className="text-emerald-500">({Math.round(s.score * 100)}%)</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
